import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getClientStopSources,
  addClientStopSource,
  deleteClientStopSource,
  StopSource,
} from '../api';

interface Props {
  open: boolean;
  clientId: number | null;
  clientName?: string;
  onClose: () => void;
}

export const ClientStopSourcesDialog: React.FC<Props> = ({ open, clientId, clientName, onClose }) => {
  const [items, setItems] = useState<StopSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pattern, setPattern] = useState('');
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getClientStopSources(clientId);
      setItems(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (open && clientId) load();
  }, [open, clientId, load]);

  const handleAdd = async () => {
    if (!clientId) return;
    if (!pattern.trim()) {
      setError('Укажите паттерн');
      return;
    }
    setAdding(true);
    setError(null);
    try {
      const added = await addClientStopSource(clientId, {
        pattern: pattern.trim(),
        note: note.trim() || undefined,
      });
      setItems(prev => [...prev.filter(i => i.pattern !== added.pattern), added]);
      setPattern('');
      setNote('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteClientStopSource(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Стоп-источники для заказчика {clientName ? `«${clientName}»` : `#${clientId}`}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Подстрочный поиск в поле «контакт» звонка. Лиды с совпавшей подстрокой не уйдут этому заказчику.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Паттерн"
            size="small"
            value={pattern}
            onChange={e => setPattern(e.target.value)}
            placeholder="kult-mebeli.online или 79806988637"
            sx={{ minWidth: 240, flex: 1 }}
          />
          <TextField
            label="Заметка"
            size="small"
            value={note}
            onChange={e => setNote(e.target.value)}
            sx={{ minWidth: 180, flex: 1 }}
          />
          <Button variant="contained" onClick={handleAdd} disabled={adding}>
            {adding ? '…' : 'Добавить'}
          </Button>
        </Box>

        {loading && <CircularProgress size={24} />}

        <List dense>
          {items.map(item => (
            <ListItem
              key={item.id}
              secondaryAction={
                <IconButton edge="end" onClick={() => handleDelete(item.id)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>{item.pattern}</Typography>}
                secondary={item.note}
              />
            </ListItem>
          ))}
          {items.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              Стоп-источники не настроены
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};
